import { Link } from 'react-router-dom';
import {
  Shield, Target, Users, Zap, Globe, Lock, Heart,
  ArrowRight, Star, TrendingUp, Award,
} from 'lucide-react';
import PageLayout from '../components/PageLayout';

/* ── Stat card ────────────────────────────────────────────────────────── */
function Stat({ value, label, sub }) {
  return (
    <div className="text-center p-6 glass-card border border-white/[0.06]">
      <div className="text-4xl font-black text-white mb-1 tabular-nums">{value}</div>
      <div className="text-sm font-semibold text-[#00E5FF] mb-0.5">{label}</div>
      {sub && <div className="text-xs text-slate-600">{sub}</div>}
    </div>
  );
}

/* ── Value card ───────────────────────────────────────────────────────── */
function Value({ icon: Icon, title, desc, color }) {
  return (
    <div className="glass-card border border-white/[0.06] p-6 group hover:-translate-y-1 transition-all duration-300">
      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform"
        style={{ background: `${color}18`, border: `1px solid ${color}28` }}
      >
        <Icon size={19} style={{ color }} />
      </div>
      <h3 className="text-base font-bold text-white mb-2">{title}</h3>
      <p className="text-slate-500 text-sm leading-relaxed">{desc}</p>
    </div>
  );
}

/* ── Team member ──────────────────────────────────────────────────────── */
function TeamMember({ initials, name, role, bio, gradient }) {
  return (
    <div className="glass-card border border-white/[0.06] p-6 text-center group hover:-translate-y-1 transition-all duration-300">
      <div
        className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center text-xl font-black text-white mx-auto mb-4 shadow-lg group-hover:shadow-xl transition-shadow`}
      >
        {initials}
      </div>
      <div className="text-sm font-bold text-white mb-0.5">{name}</div>
      <div className="text-xs text-[#00E5FF] font-medium mb-3">{role}</div>
      <p className="text-xs text-slate-500 leading-relaxed">{bio}</p>
    </div>
  );
}

const TEAM = [
  {
    initials: 'AK',
    name: 'Alex Kim',
    role: 'CEO & Co-Founder',
    bio: 'Former blockchain security researcher at Trail of Bits. 8 years in Web3 security auditing and smart contract analysis.',
    gradient: 'from-[#00E5FF] to-[#7C3AED]',
  },
  {
    initials: 'SR',
    name: 'Sara Reyes',
    role: 'CTO & Co-Founder',
    bio: 'Ex-Coinbase infrastructure engineer. Built real-time transaction monitoring systems processing 10M+ events per day.',
    gradient: 'from-[#7C3AED] to-[#f43f5e]',
  },
  {
    initials: 'MT',
    name: 'Marcus Tran',
    role: 'Head of Research',
    bio: 'PhD in Applied Cryptography. Published researcher on DeFi exploit patterns and on-chain fraud detection methodologies.',
    gradient: 'from-[#f59e0b] to-[#f43f5e]',
  },
  {
    initials: 'LW',
    name: 'Lena Wu',
    role: 'Head of Product',
    bio: 'Former product lead at Chainalysis. Obsessed with making complex blockchain data accessible to every security team.',
    gradient: 'from-[#00E5FF] to-[#06b6d4]',
  },
  {
    initials: 'JO',
    name: 'James Okafor',
    role: 'Lead Engineer',
    bio: 'Full-stack engineer with deep expertise in blockchain data infrastructure, high-throughput APIs, and security tooling.',
    gradient: 'from-[#8b5cf6] to-[#7C3AED]',
  },
  {
    initials: 'PM',
    name: 'Priya Mehta',
    role: 'Head of Compliance',
    bio: 'Certified AML specialist with 10 years in regulatory technology. Bridges the gap between blockchain data and compliance requirements.',
    gradient: 'from-[#10b981] to-[#059669]',
  },
];

const VALUES = [
  {
    icon: Shield,
    title: 'Security First',
    desc: 'Every decision is made with security as the primary lens. We eat our own dog food — ZenithCipher is secured by ZenithCipher.',
    color: '#00E5FF',
  },
  {
    icon: Target,
    title: 'Accuracy Over Speed',
    desc: 'We would rather give you a precise risk assessment than a fast wrong one. Our engine is tuned for minimal false positives.',
    color: '#7C3AED',
  },
  {
    icon: Heart,
    title: 'User Obsession',
    desc: 'Security tools should be a joy to use. We obsess over UX so your team spends time on insights, not fighting the interface.',
    color: '#f43f5e',
  },
  {
    icon: Globe,
    title: 'Open Web3',
    desc: 'We believe in a safe, open, permissionless Web3. Our free tier exists to make security intelligence accessible to everyone.',
    color: '#f59e0b',
  },
  {
    icon: Lock,
    title: 'Data Privacy',
    desc: "We never sell your data. Scan reports belong to you. We're transparent about how data flows through our platform.",
    color: '#10b981',
  },
  {
    icon: Zap,
    title: 'Move Fast, Stay Safe',
    desc: 'The best security tool is the one your team actually uses. We ship fast, iterate on feedback, and never compromise on safety.',
    color: '#8b5cf6',
  },
];

const MILESTONES = [
  { year: 'Jan 2025', label: 'Founded',          desc: 'ZenithCipher incorporated in San Francisco by Alex and Sara.' },
  { year: 'Mar 2025', label: 'First API',         desc: 'Wallet risk API v1 shipped. First 50 users in the first week.' },
  { year: 'Jun 2025', label: 'Seed Round',        desc: '$3M seed round led by Paradigm to accelerate product and team growth.' },
  { year: 'Sep 2025', label: '10K Scans / Day',   desc: 'Platform reached 10,000 daily wallet scans milestone. Risk Engine v2 launched.' },
  { year: 'Jan 2026', label: 'Enterprise Launch', desc: 'Enterprise plan and custom integrations launched for compliance teams.' },
  { year: 'May 2026', label: '500+ Teams',        desc: 'Serving 500+ security teams across 40 countries. 1M+ scans completed.' },
];

export default function About() {
  return (
    <PageLayout
      badge="Our Story"
      badgeColor="cyan"
      title={<>Building the future of<br /><span className="text-[#00E5FF]">Web3 security intelligence</span></>}
      subtitle="We started ZenithCipher because every Web3 team deserved enterprise-grade wallet risk intelligence — not just the ones who could afford it."
    >
      {/* Stats */}
      <section className="py-16 max-w-5xl mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat value="500+"  label="Security Teams"   sub="in 40+ countries" />
          <Stat value="1M+"   label="Wallets Scanned"  sub="and counting" />
          <Stat value="99.9%" label="Uptime SLA"       sub="production reliability" />
          <Stat value="< 1s"  label="Avg Scan Time"    sub="real-time analysis" />
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 bg-[#080c16]/60 border-y border-white/[0.05]">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-semibold text-[#7C3AED] bg-[#7C3AED]/10 border border-[#7C3AED]/20 px-3 py-1.5 rounded-full mb-5">
                <Star size={11} /> Our Story
              </div>
              <h2 className="text-3xl font-bold text-white mb-5 leading-tight">
                Built by security researchers<br />who got frustrated
              </h2>
              <div className="space-y-4 text-slate-400 text-sm leading-relaxed">
                <p>
                  In 2024, our co-founders Alex and Sara were working on a DeFi protocol security audit.
                  Every time they needed to check if a wallet had a suspicious history, they were cobbling
                  together Etherscan, Dune Analytics, and a spreadsheet. It was slow, error-prone, and
                  completely unscalable.
                </p>
                <p>
                  They searched for a tool that could give them a structured risk score for any wallet —
                  fast, accurate, and developer-friendly. It didn't exist at a price point that made sense
                  for most teams.
                </p>
                <p>
                  So they built it. ZenithCipher launched in January 2025 with a simple promise:
                  <strong className="text-white"> enterprise-grade wallet intelligence for every Web3 team</strong>,
                  starting at $0.
                </p>
              </div>
            </div>
            <div className="space-y-0">
              {MILESTONES.map(({ year, label, desc }, i) => (
                <div key={year} className="relative pl-8 pb-8 last:pb-0 border-l border-white/[0.07] last:border-l-transparent">
                  <div className="absolute -left-1.5 top-1 w-3 h-3 rounded-full bg-[#00E5FF]/30 border-2 border-[#00E5FF]" />
                  <div className="text-[10px] font-bold text-[#00E5FF] tracking-widest mb-0.5">{year}</div>
                  <div className="text-sm font-bold text-white mb-1">{label}</div>
                  <div className="text-xs text-slate-500 leading-relaxed">{desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 max-w-6xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 text-xs font-semibold text-[#00E5FF] bg-[#00E5FF]/10 border border-[#00E5FF]/20 px-3 py-1.5 rounded-full mb-4">
            <Heart size={11} /> Our Values
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
            What we stand for
          </h2>
          <p className="text-slate-400 text-base max-w-lg mx-auto">
            These aren't just words on a wall — they're the principles that guide every product decision we make.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {VALUES.map(v => <Value key={v.title} {...v} />)}
        </div>
      </section>

      {/* Team */}
      <section className="py-20 bg-[#080c16]/60 border-y border-white/[0.05]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-[#7C3AED] bg-[#7C3AED]/10 border border-[#7C3AED]/20 px-3 py-1.5 rounded-full mb-4">
              <Users size={11} /> The Team
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              Meet the people behind ZenithCipher
            </h2>
            <p className="text-slate-400 text-base max-w-lg mx-auto">
              A small, focused team of security researchers, engineers, and product builders — united by a passion for Web3 safety.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {TEAM.map(m => <TeamMember key={m.name} {...m} />)}
          </div>
          <p className="text-center text-slate-600 text-sm mt-8">
            Want to join us?{' '}
            <Link to="/careers" className="text-[#00E5FF] hover:underline">
              We're hiring →
            </Link>
          </p>
        </div>
      </section>

      {/* Investors & recognition */}
      <section className="py-20 max-w-5xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 text-xs font-semibold text-amber-400 bg-amber-500/10 border border-amber-500/20 px-3 py-1.5 rounded-full mb-4">
            <Award size={11} /> Backed & Recognized
          </div>
          <h2 className="text-3xl font-bold text-white mb-3">Trusted by the best in the space</h2>
        </div>
        <div className="flex flex-wrap justify-center items-center gap-10 mb-14">
          {['Paradigm', 'Multicoin Capital', 'a16z crypto', 'Y Combinator', 'Coinbase Ventures'].map(name => (
            <div key={name} className="text-slate-500 hover:text-slate-300 font-semibold text-sm transition-colors cursor-default">
              {name}
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="glass-card border border-[#00E5FF]/15 p-10 text-center rounded-2xl">
          <TrendingUp size={28} className="text-[#00E5FF] mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-white mb-2">Ready to secure your Web3 stack?</h3>
          <p className="text-slate-400 text-sm mb-6 max-w-md mx-auto">
            Join 500+ teams using ZenithCipher for wallet intelligence. Free plan, no credit card required.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-2 bg-[#00E5FF] hover:bg-[#00cce5] text-black font-bold px-6 py-3 rounded-xl transition-all shadow-lg shadow-[#00E5FF]/20 text-sm group"
            >
              Get Started Free <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 border border-white/10 hover:border-white/20 bg-white/[0.04] text-slate-300 font-medium px-6 py-3 rounded-xl transition-all text-sm"
            >
              Talk to Sales
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
